import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const fmt = (n) => (n ?? 0).toLocaleString();
export const fmtDT = (s) => (s ? String(s).replace('T', ' ').slice(0, 16) : '—');
export const UNAVAILABLE = (what) =>
  `${what} data is temporarily unavailable. This can happen if your account is not connected to the upstream LevelUp API. Please try again shortly or contact your administrator.`;

export function usePii() {
  const [pii, setPii] = useState(false);
  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPii(r.data?.pii_allowed === true)).catch(() => setPii(false));
  }, []);
  return pii;
}

export function SABadge({ name }) {
  if (!name) return <span style={{ color: '#94a3b8' }}>—</span>;
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: '#eef2ff', color: '#4338ca', whiteSpace: 'nowrap' }}>{name}</span>;
}

export function StatusPill({ status }) {
  const s = String(status || '').toLowerCase();
  const map = { completed: ['#dcfce7', '#15803d'], 'in progress': ['#fef9c3', '#a16207'], enrolled: ['#f1f5f9', '#64748b'] };
  const [bg, fg] = map[s] || ['#f1f5f9', '#64748b'];
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg }}>{status || '—'}</span>;
}

export const Spinner = () => <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;

export function ErrorBanner({ message, testId = 'report-error' }) {
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '24px 20px' }}>
      <AlertCircle size={18} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
      <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>{message}</p>
    </div>
  );
}

export function PaginationBar({ page, totalPages, total, perPage, onPage, testId }) {
  if (totalPages <= 1) return null;
  const start = Math.max(1, page - 2), end = Math.min(totalPages, page + 2);
  const pages = []; for (let i = start; i <= end; i++) pages.push(i);
  const from = (page - 1) * perPage + 1, to = Math.min(page * perPage, total);
  return (
    <div className="pagination" data-testid={testId}>
      <span className="pagination-info">{from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</span>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className="page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      {pages.map(p => <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => onPage(p)}>{p}</button>)}
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
      <button className="page-btn" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
    </div>
  );
}

// Dynamic table — derives columns from row keys when `columns` not given (for
// catalog-server feeds with unconfirmed field lists).
export function DynamicTable({ rows, columns, testId, max = 12 }) {
  const cols = columns || (rows[0] ? Object.keys(rows[0]).slice(0, max).map(k => ({ key: k, label: k.replace(/_/g, ' ') })) : []);
  const cell = (row, c) => {
    if (c.render) return c.render(row);
    const v = row[c.key];
    if (v == null || v === '') return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };
  return (
    <div className="table-scroll">
      <table data-testid={testId}>
        <thead><tr>{cols.map(c => <th key={c.key} style={{ textTransform: 'capitalize' }}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} data-testid={`${testId}-row-${i}`}>
              {cols.map(c => {
                const v = row[c.key];
                return <td key={c.key} style={{ fontSize: 12, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')}>{cell(row, c)}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
